import { deleteCookie, readCookie } from "@/lib/cookie";

/**
 * UTM / click-id attribution carrier for the portal.
 *
 * Exact sibling of `apps/app/src/lib/referral/attribution.ts`, for the same
 * reason: the payload is captured on apps/web
 * (`apps/web/src/lib/attribution/use-utm-attribution.ts`), which stores it in
 * ITS OWN localStorage — unreadable from portal.pontimed.app — and mirrors it
 * into a cookie scoped to the parent `pontimed.<tld>`. That cookie is the only
 * thing that survives the hop to this app.
 *
 * We forward it explicitly in the signup body as well: the Capacitor build
 * runs from `file://` with no shared cookie jar, so the cookie path alone
 * would silently drop attribution for every native signup.
 *
 * Before this existed, `Lead.utmSource/utmMedium/utmCampaign/gclid/fbclid` had
 * zero writers repo-wide — the columns were in the schema but no campaign
 * could ever be tied to a signup. See _docs/social-automation-plan.md §11.
 */
const UTM_COOKIE = "pontimed_utm";

export interface AttributionTouch {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
  gclid?: string;
  fbclid?: string;
  ttclid?: string;
  li_fat_id?: string;
  ts?: string;
}

export interface AttributionPayload {
  first?: AttributionTouch;
  last?: AttributionTouch;
  /**
   * The visitor had granted the cookie-consent `marketing` category when the
   * click ids were captured on apps/web. Forwarded as-is; the API stamps
   * `Lead.adsConsentAt` from it and never sends an ad conversion without it.
   */
  adsConsent?: boolean;
}

/**
 * Never throws. A malformed or tampered cookie is dropped rather than sent to
 * the API — attribution is analytics, and it must never be able to fail a
 * registration.
 */
export function getCapturedAttribution(): AttributionPayload | undefined {
  const raw = readCookie(UTM_COOKIE);
  if (!raw) return undefined;
  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(raw));
    if (!parsed || typeof parsed !== "object") return undefined;
    const obj = parsed as AttributionPayload;
    return obj.first || obj.last ? obj : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Drop the payload once attributed, so a later signup on the same device
 * (family member, shared browser) is not credited to the same campaign.
 */
export function clearCapturedAttribution(): void {
  deleteCookie(UTM_COOKIE);
}
